// GtOutreach.js — Auto outreach (C1-OUTREACH, 3 Nov 2026).
//
// The home of c84: hiring signals into client outreach. Part 1 is the weekly hiring check: every Monday each role
// with a live Insights article is searched for job adverts from the last 7 days, and each company hiring for it
// becomes a prospect client (or updates one) with the advert saved on the client. Nobody is contacted yet.
// Parts 2 (who to send to) and 3 (sending, fully autonomous) add their tabs here as they are built.
// Campaigns may join this screen later.
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthFetch } from './GtLexaAdmin';

function ukDay(v) {
  if (!v) return '';
  try { return new Date(v).toLocaleDateString('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short' }); } catch (e) { return ''; }
}
const plural = (n, one, many) => n + ' ' + (n === 1 ? one : many);
const host = (u) => { try { return new URL(u).hostname.replace(/^www\./, ''); } catch (e) { return ''; } };

function HiringNow({ authFetch, navigate }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const load = useCallback(() => authFetch('/api/insights/admin/hiring-check').then(setD).catch((e) => setMsg(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  const latest = d && d.runs[0];
  const running = !!(d && (d.running || (latest && latest.status === 'running')));
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [running, load]);

  const companies = useMemo(() => {
    if (!d) return [];
    const seen = new Map();
    const out = [];
    d.hits.forEach((h) => {
      const k = h.client_id || h.company;
      if (!seen.has(k)) { seen.set(k, { key: k, client_id: h.client_id, ref: h.client_ref, name: h.client_name || h.company, isNew: false, location: h.location, ads: [] }); out.push(seen.get(k)); }
      const c = seen.get(k);
      if (h.client_new) c.isNew = true;
      if (!c.location && h.location) c.location = h.location;
      c.ads.push(h);
    });
    const t = q.trim().toLowerCase();
    return t ? out.filter((c) => (c.name + ' ' + c.ads.map((a) => a.role_title + ' ' + (a.posting_title || '')).join(' ')).toLowerCase().includes(t)) : out;
  }, [d, q]);

  async function run() {
    setMsg('');
    try { await authFetch('/api/insights/admin/hiring-check/run', { method: 'POST' }); await load(); } catch (e) { setMsg(e.message); }
  }

  if (!d) return msg ? <div className="gtiv-card gtiv-err">{msg}</div> : <div className="gtiv-card gtiv-loading">Loading…</div>;
  const last = d.runs.find((r) => r.status === 'done');
  const shown = d.found_run || d.runs.find((r) => r.id === d.last_id);
  const roles = new Set(d.hits.map((h) => h.role_title)).size;
  return (
    <>
      <section className="gtob-hero">
        <div className="gtob-hero-t">
          <b>Hiring check</b>
          <span>Every Monday at 07:00, each role with a live Insights article is searched for job adverts from the last 7 days. Each company hiring for it is added as a prospect client, or updated if it is one already, with the advert saved on the client. Nobody is contacted.</span>
        </div>
        <button type="button" className="gt-btn gt-btn--primary" disabled={running || !d.live_roles} title={!d.live_roles ? 'No articles are live yet' : ''} onClick={run}>{running ? 'Checking…' : 'Run the check now'}</button>
      </section>

      {running && (
        <div className="gtiv-card gtob-prog"><div className="gtjf-prog"><div className="gtjf-bar"><i style={{ width: (latest && latest.roles ? Math.max(5, Math.round((latest.roles_done / latest.roles) * 100)) : 5) + '%' }} /></div><span>Checking {latest ? latest.roles_done : 0} of {latest ? latest.roles : '…'} roles. You can leave this screen.</span></div></div>
      )}
      {!running && latest && latest.status === 'failed' && <div className="gtiv-card gtiv-err">The last check did not finish: {latest.error}</div>}
      {msg && <div className="gtiv-card gtiv-err">{msg}</div>}

      <div className="gtob-stats">
        <div className="gtob-stat"><em>Roles with a live article</em><b>{d.live_roles}</b></div>
        <div className="gtob-stat"><em>Adverts found</em><b>{shown ? shown.postings : 0}</b></div>
        <div className="gtob-stat"><em>Companies hiring</em><b>{shown ? shown.companies : 0}</b></div>
        <div className="gtob-stat gtob-stat--g"><em>New prospects</em><b>{shown ? shown.new_clients : 0}</b></div>
      </div>
      <p className="gtob-when">
        {last ? <>Last check {ukDay(last.finished_at || last.started_at)}{last.id !== d.last_id && d.found_at ? ', nothing new. Showing what was found ' + ukDay(d.found_at) + '.' : '.'}</> : d.live_roles ? (d.runs.length ? 'No check has finished yet.' : 'Not run yet. The first check runs next Monday, or run it now.') : 'No articles are live yet. The check starts once one is.'}
      </p>

      {companies.length > 0 || q ? (
        <>
          <div className="gtob-bar">
            <h2>{plural(companies.length, 'company', 'companies')} hiring{roles ? ' for ' + plural(roles, 'role', 'roles') + ' we have written about' : ''}</h2>
            <input className="gtiv-in gtob-search" type="search" placeholder="Search companies and roles" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search companies and roles" />
          </div>
          <ul className="gtob-list">
            {companies.map((c) => (
              <li key={c.key} className="gtob-co">
                <div className="gtob-co-h">
                  <span className="gtob-av" aria-hidden="true">{String(c.name || '?').charAt(0).toUpperCase()}</span>
                  <div className="gtob-co-t">
                    {c.client_id ? <button type="button" className="gtob-name" onClick={() => navigate('/clients/' + c.client_id)}>{c.name}</button> : <b className="gtob-name">{c.name}</b>}
                    <em>{[c.location, plural(c.ads.length, 'advert', 'adverts')].filter(Boolean).join(' · ')}</em>
                  </div>
                  {c.isNew ? <span className="gtob-chip gtob-chip--g">New prospect</span> : <span className="gtob-chip">Existing client</span>}
                </div>
                <ul className="gtob-ads">
                  {c.ads.map((a) => (
                    <li key={a.id}>
                      <div className="gtob-ad-t"><span>Hiring for</span> <b>{a.role_title}</b></div>
                      <a href={a.posting_url} target="_blank" rel="noopener noreferrer" title={a.posting_url}>{a.posting_title || 'The advert'}<i>{host(a.posting_url)} &#8599;</i></a>
                      {a.named_contact && a.named_contact.name && <div className="gtob-ad-c">Named in the advert: <b>{a.named_contact.name}</b>{a.named_contact.title ? ', ' + a.named_contact.title : ''}</div>}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
            {companies.length === 0 && <li className="gtiv-card gtob-empty">No companies match.</li>}
          </ul>
        </>
      ) : last ? <div className="gtiv-card gtob-empty">No companies found yet.</div> : null}
    </>
  );
}

// C1-PEOPLE (c84 part 2): who to send the article to at each company, and where each person came from.
const SOURCE = { advert: 'Named in the advert', gtco: 'Already in GTCO', sonar: 'Sonar', apollo: 'Apollo', web: 'Found on the web' };
function People({ authFetch, navigate }) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const load = useCallback(() => authFetch('/api/outreach/people').then(setD).catch((e) => setMsg(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  const latest = d && d.runs[0];
  const running = !!(d && (d.running || (latest && latest.status === 'running')));
  useEffect(() => { if (!running) return undefined; const t = setInterval(load, 4000); return () => clearInterval(t); }, [running, load]);
  const list = useMemo(() => {
    if (!d) return [];
    const by = new Map(d.companies.map((c) => [c.client_id, { ...c, people: [] }]));
    d.people.forEach((p) => { const c = by.get(p.client_id); if (c) c.people.push(p); });
    const t = q.trim().toLowerCase();
    const all = Array.from(by.values());
    return t ? all.filter((c) => (c.name + ' ' + (c.role_title || '') + ' ' + c.people.map((p) => p.full_name + ' ' + (p.title || '')).join(' ')).toLowerCase().includes(t)) : all;
  }, [d, q]);
  async function run() {
    setMsg('');
    try { await authFetch('/api/outreach/people/run', { method: 'POST' }); await load(); } catch (e) { setMsg(e.message); }
  }
  if (!d) return msg ? <div className="gtiv-card gtiv-err">{msg}</div> : <div className="gtiv-card gtiv-loading">Loading…</div>;
  const sendable = d.people.filter((p) => p.sendable).length;
  return (
    <>
      <section className="gtob-hero">
        <div className="gtob-hero-t">
          <b>Who to send to</b>
          <span>After each Monday hiring check, up to 3 people at every company hiring: anyone the advert names first, then people already in GTCO, then leadership found through Apollo. Under 50 people: founders, the CTO and technical directors or heads. Over 100: talent and people leadership. 50 to 100: both. Engineering managers are only written to when the advert names them. Only verified work emails on the company's own domain can be sent to. Nobody is contacted yet.</span>
        </div>
        <button type="button" className="gt-btn gt-btn--primary" disabled={running || !d.waiting} title={!d.waiting ? 'Every company found so far has been looked at' : ''} onClick={run}>{running ? 'Looking…' : d.waiting ? 'Find people now (' + d.waiting + ')' : 'Nothing waiting'}</button>
      </section>
      {!d.apollo && <div className="gtiv-card gtob-warn">Apollo is not set up, so only people named in adverts or already in GTCO can be found. Add APOLLO_API_KEY in Railway.</div>}
      {running && <div className="gtiv-card gtob-prog"><div className="gtjf-prog"><div className="gtjf-bar"><i style={{ width: (latest && latest.companies ? Math.max(5, Math.round((latest.done / latest.companies) * 100)) : 5) + '%' }} /></div><span>Looking at {latest ? latest.done : 0} of {latest ? latest.companies : '…'} companies. You can leave this screen.</span></div></div>}
      {msg && <div className="gtiv-card gtiv-err">{msg}</div>}
      <div className="gtob-stats">
        <div className="gtob-stat"><em>Companies looked at</em><b>{d.companies.length}</b></div>
        <div className="gtob-stat"><em>People found</em><b>{d.people.length}</b></div>
        <div className="gtob-stat gtob-stat--g"><em>Ready to send to</em><b>{sendable}</b></div>
        <div className="gtob-stat"><em>Waiting to be looked at</em><b>{d.waiting}</b></div>
      </div>
      <p className="gtob-when">{latest ? <>Last looked {ukDay(latest.finished_at || latest.started_at)}{latest.credits ? ', ' + plural(latest.credits, 'Apollo credit', 'Apollo credits') + ' used' : ''}{latest.error ? '. ' + latest.error : '.'}</> : 'Not run yet. It runs after the next Monday hiring check, or run it now.'}</p>
      {list.length > 0 || q ? (
        <>
          <div className="gtob-bar">
            <h2>{plural(list.length, 'company', 'companies')}</h2>
            <input className="gtiv-in gtob-search" type="search" placeholder="Search companies and people" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search companies and people" />
          </div>
          <ul className="gtob-list">
            {list.map((c) => (
              <li key={c.client_id} className="gtob-co">
                <div className="gtob-co-h">
                  <span className="gtob-av" aria-hidden="true">{String(c.name || '?').charAt(0).toUpperCase()}</span>
                  <div className="gtob-co-t">
                    <button type="button" className="gtob-name" onClick={() => navigate('/clients/' + c.client_id)}>{c.name}</button>
                    <em>{[c.role_title ? 'Hiring for ' + c.role_title : null, plural(c.people.length, 'person', 'people')].filter(Boolean).join(' · ')}</em>
                  </div>
                  {c.posting_url && <a className="gtob-adv" href={c.posting_url} target="_blank" rel="noopener noreferrer">The advert &#8599;</a>}
                </div>
                {c.people.length > 0 ? (
                  <ul className="gtob-ppl">
                    {c.people.map((p) => (
                      <li key={p.id}>
                        <div className="gtob-p-t"><b>{p.full_name}</b><span>{p.title || 'Title not known'}</span></div>
                        <div className="gtob-p-e">{p.email || <i>No email kept</i>}{p.linkedin_url && <a href={p.linkedin_url} target="_blank" rel="noopener noreferrer">LinkedIn &#8599;</a>}</div>
                        <span className="gtob-src">{SOURCE[p.source] || p.source}</span>
                        {p.sendable ? <span className="gtob-chip gtob-chip--g">Ready</span> : <span className="gtob-chip gtob-chip--a" title={p.reason || ''}>{p.reason || 'Not sendable'}</span>}
                      </li>
                    ))}
                  </ul>
                ) : <p className="gtob-none">{c.note || 'Nobody found yet'}</p>}
              </li>
            ))}
            {list.length === 0 && <li className="gtiv-card gtob-empty">No companies match.</li>}
          </ul>
        </>
      ) : latest ? <div className="gtiv-card gtob-empty">No companies looked at yet.</div> : null}
    </>
  );
}

// C1-SEND (c84 part 3): what has been sent, and what has not and why.
function useSending(authFetch) {
  const [d, setD] = useState(null);
  const [msg, setMsg] = useState('');
  const load = useCallback(() => authFetch('/api/outreach/sending').then(setD).catch((e) => setMsg(e.message)), [authFetch]);
  useEffect(() => { load(); }, [load]);
  return { d, setD, msg, setMsg, load };
}
function when(v) { try { return new Date(v).toLocaleString('en-GB', { timeZone: 'Europe/London', weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } }
const bodyText = (t) => String(t || '').replace(/\[\[([^\]]+)\]\]\{\{(article|library)\}\}/g, '$1');
function SendBar({ d, navigate }) {
  const mine = (d.held || []).filter((m) => m.status === 'needs_you').length;
  return (
    <>
      {!d.live && <div className="gtiv-card gtob-warn">This copy is not production, so nothing is sent from here.</div>}
      {d.live && !d.senders && <div className="gtiv-card gtob-warn">No sending address is marked for client outreach, so nothing can go. Add one in Settings, Sending.</div>}
      {mine > 0 && <div className="gtiv-card gtob-mine"><span>{mine === 1 ? '1 email is' : mine + ' emails are'} written and waiting for you to read before {mine === 1 ? 'it goes' : 'they go'}.</span>{navigate && <button type="button" className="gt-btn gt-btn--primary" onClick={() => navigate('/outreach/not-sent')}>Read and send</button>}</div>}
      <div className="gtob-stats">
        <div className="gtob-stat"><em>Sent today</em><b>{d.today} <small>of {d.cap}</small></b></div>
        <button type="button" className="gtob-stat gtob-stat--go" disabled={!navigate} onClick={() => navigate && navigate('/outreach/not-sent')}><em>Waiting for you</em><b>{mine}</b></button>
        <button type="button" className="gtob-stat gtob-stat--go" disabled={!navigate} onClick={() => navigate && navigate('/outreach/people')}><em>People waiting</em><b>{d.waiting}</b></button>
        <div className="gtob-stat"><em>Sending now</em><b className="gtob-small">{d.in_window ? 'Yes, until 17:30' : 'Weekdays 09:00 to 17:30'}</b></div>
      </div>
      <p className="gtob-when">The daily cap starts at 20 and rises by 10 a week to 50 while the sending domain is new. Each person gets the article, then one follow-up 5 days later unless anyone at their company replies.</p>
    </>
  );
}
function Sent({ authFetch, navigate }) {
  const { d, msg } = useSending(authFetch);
  const [open, setOpen] = useState(null);
  if (!d) return msg ? <div className="gtiv-card gtiv-err">{msg}</div> : <div className="gtiv-card gtiv-loading">Loading…</div>;
  const state = (m) => m.send_status === 'replied' ? ['Replied', 'g'] : m.last_event === 'bounced' ? ['Bounced', 'r'] : m.last_event === 'complained' ? ['Marked as spam', 'r'] : m.send_status === 'unsubscribed' ? ['Unsubscribed', 'a'] : m.clicks > 0 ? ['Clicked', 'g'] : m.last_event === 'delivered' ? ['Delivered', ''] : ['Sent', ''];
  const clicked = d.sent.filter((m) => m.clicks > 0).length;
  const opened = (m) => (m.clicked_links || []).map((l) => l === 'article' ? 'the article' : 'the role page').join(' and ');
  return (
    <>
      <SendBar d={d} navigate={navigate} />
      <div className="gtob-bar"><h2>{plural(d.sent.length, 'email', 'emails')} sent{d.sent.length ? ' · ' + clicked + ' clicked' : ''}</h2></div>
      {d.sent.length === 0 ? <div className="gtiv-card gtob-empty">Nothing sent yet.</div> : (
        <ul className="gtob-sent">
          {d.sent.map((m) => { const [st, tone] = state(m); return (
            <li key={m.id} className={open === m.id ? 'gtob-sent--open' : ''}>
              <button type="button" className="gtob-sent-row" onClick={() => setOpen(open === m.id ? null : m.id)} aria-expanded={open === m.id}>
                <span className="gtob-sent-who"><b>{m.full_name}</b><em>{m.title ? m.title + ', ' : ''}{m.company}</em></span>
                <span className="gtob-sent-s"><b>{m.subject}</b><em>{m.step > 1 ? 'Follow-up' : 'First email'} · {m.role_title}</em>{m.clicks > 0 && <em className="gtob-click">Opened {opened(m)}, {when(m.first_click_at)}{m.clicks > 1 ? ' · ' + m.clicks + ' clicks, last ' + when(m.last_click_at) : ''}</em>}</span>
                <span className="gtob-sent-t">{when(m.sent_at)}</span>
                <span className={'gtob-chip' + (tone === 'g' ? ' gtob-chip--g' : tone === 'r' ? ' gtob-chip--r' : tone === 'a' ? ' gtob-chip--a' : '')}>{st}</span>
              </button>
              {open === m.id && (
                <div className="gtob-sent-b">
                  <p className="gtob-sent-meta">From {m.from_email} to {m.to_email} · <button type="button" className="gtiv-link" onClick={() => navigate('/clients/' + m.client_id)}>Open {m.company}</button></p>
                  <pre>{bodyText(m.body_text)}</pre>
                </div>
              )}
            </li>
          ); })}
        </ul>
      )}
    </>
  );
}
function NotSent({ authFetch, navigate }) {
  const { d, setD, msg, setMsg } = useSending(authFetch);
  const [busy, setBusy] = useState('');
  const [open, setOpen] = useState(null);
  async function act(id, a) {
    setBusy(id + a); setMsg('');
    try { setD(await authFetch('/api/outreach/sending/' + id + '/' + a, { method: 'POST' })); } catch (e) { setMsg(e.message); }
    setBusy('');
  }
  const [sel, setSel] = useState(() => new Set());
  const [note, setNote] = useState('');
  const pick = (id) => setSel((p) => { const n = new Set(p); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  async function bulk(a) {
    const ids = Array.from(sel);
    if (!ids.length) return;
    setBusy('bulk' + a); setMsg(''); setNote('');
    try {
      const out = await authFetch('/api/outreach/sending/bulk', { method: 'POST', body: JSON.stringify({ ids, action: a }) });
      setD(out); setSel(new Set());
      const n = out.bulk ? out.bulk.done : 0;
      const did = a === 'send' ? (out.in_window ? plural(n, 'email', 'emails') + ' sent' : plural(n, 'email', 'emails') + ' will go from 09:00 on the next weekday') : plural(n, 'email', 'emails') + ' not sent';
      setNote(did + '.' + (out.bulk && out.bulk.problems.length ? ' ' + out.bulk.problems.join(' ') : ''));
    } catch (e) { setMsg(e.message); }
    setBusy('');
  }
  async function clear(id) {
    setBusy(id ? 'p' + id : 'all'); setMsg('');
    try { setD(await authFetch('/api/outreach/not-emailed/' + (id ? id + '/' : '') + 'dismiss', { method: 'POST' })); } catch (e) { setMsg(e.message); }
    setBusy('');
  }
  if (!d) return msg ? <div className="gtiv-card gtiv-err">{msg}</div> : <div className="gtiv-card gtiv-loading">Loading…</div>;
  return (
    <>
      {msg && <div className="gtiv-card gtiv-err">{msg}</div>}
      {note && <div className="gtiv-card gtob-note">{note}</div>}
      <div className="gtob-bar"><h2>{plural(d.held.length, 'email', 'emails')} waiting for you</h2>
        {d.held.length > 0 && (() => { const live = d.held.filter((m) => sel.has(m.id)); const all = live.length === d.held.length; return (
          <span className="gtob-bulk">
            <label className="gtob-check"><input type="checkbox" checked={all} ref={(el) => { if (el) el.indeterminate = live.length > 0 && !all; }} onChange={() => setSel(all ? new Set() : new Set(d.held.map((m) => m.id)))} />{live.length ? live.length + ' selected' : 'Select all'}</label>
            {live.length > 0 && <>
              <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy} onClick={() => bulk('send')}>{busy === 'bulksend' ? 'Sending…' : 'Send ' + live.length}</button>
              <button type="button" className="gtiv-link gtst-rm" disabled={!!busy} onClick={() => bulk('skip')}>{busy === 'bulkskip' ? 'Working…' : "Don't send " + live.length}</button>
            </>}
          </span>); })()}
      </div>
      {d.held.length === 0 ? <div className="gtiv-card gtob-empty">Nothing is waiting. Clean emails go by themselves.</div> : (
        <ul className="gtob-sent">
          {d.held.map((m) => (
            <li key={m.id} className={'gtob-sent--open' + (sel.has(m.id) ? ' gtob-picked' : '')}>
              <div className="gtob-sent-row gtob-sent-row--click" role="button" tabIndex={0} aria-expanded={open === m.id}
                onClick={() => setOpen(open === m.id ? null : m.id)} onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); setOpen(open === m.id ? null : m.id); } }}>
                <span className="gtob-sent-who gtob-sent-who--pick"><input type="checkbox" aria-label={'Select ' + m.full_name} checked={sel.has(m.id)} onClick={(e) => e.stopPropagation()} onChange={() => pick(m.id)} /><span><b>{m.full_name}</b><em>{m.title ? m.title + ', ' : ''}{m.company}</em></span></span>
                <span className="gtob-sent-s"><b>{m.subject || (m.step > 1 ? 'Follow-up' : 'First email')}</b><em>{m.status === 'failed' ? m.error : (m.flags || []).map((f) => f.text).join(' · ')}</em></span>
                <span className="gtob-held-a" onClick={(e) => e.stopPropagation()}>
                  <button type="button" className="gtiv-link" onClick={() => setOpen(open === m.id ? null : m.id)}>{open === m.id ? 'Hide' : 'Read it'}</button>
                  <button type="button" className="gt-btn" disabled={!!busy} onClick={() => act(m.id, 'rewrite')}>{busy === m.id + 'rewrite' ? 'Writing…' : 'Write again'}</button>
                  {m.status === 'needs_you' && <button type="button" className="gt-btn gt-btn--primary" disabled={!!busy} onClick={() => act(m.id, 'send')}>Send anyway</button>}
                  <button type="button" className="gtiv-link gtst-rm" disabled={!!busy} onClick={() => act(m.id, 'skip')}>Don't send</button>
                </span>
              </div>
              {open === m.id && <div className="gtob-sent-b"><pre>{bodyText(m.body_text) || 'Nothing written.'}</pre></div>}
            </li>
          ))}
        </ul>
      )}
      <div className="gtob-bar gtob-bar--gap"><h2>{plural(d.not_sendable.length, 'person', 'people')} not emailed</h2>
        {d.not_sendable.length > 0 && <button type="button" className="gt-btn" disabled={!!busy} onClick={() => clear(null)}>{busy === 'all' ? 'Clearing…' : 'Clear them all'}</button>}</div>
      {d.not_sendable.length === 0 ? <div className="gtiv-card gtob-empty">Nobody here.</div> : (
        <ul className="gtob-ppl gtob-ppl--flat">
          {d.not_sendable.map((p) => (
            <li key={p.id}>
              <div className="gtob-p-t"><b>{p.full_name}</b><span>{p.title || 'Title not known'}</span></div>
              <div className="gtob-p-e"><button type="button" className="gtiv-link" onClick={() => navigate('/clients/' + p.client_id)}>{p.company}</button>{p.email ? <span>{p.email}</span> : null}</div>
              <span className="gtob-src">{p.role_title}</span>
              <span className="gtob-chip gtob-chip--a" title={p.stop_reason || p.reason || ''}>{p.stop_reason || p.reason || 'Existing client'}</span>
              <button type="button" className="gtob-x" aria-label={'Clear ' + p.full_name} title="Clear from this list" disabled={!!busy} onClick={() => clear(p.id)}>×</button>
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

export default function GtOutreach({ getToken }) {
  const authFetch = useAuthFetch(getToken);
  const navigate = useNavigate();
  const { tab } = useParams();
  const t = ['people', 'sent', 'not-sent'].includes(tab) ? tab : 'hiring';
  return (
    <div className="gtc-wrap gtob">
      <div className="gtc-head">
        <div>
          <h1 className="gtc-title">Auto outreach</h1>
          <p className="gtc-sub">Companies hiring for roles we have written about, found every week, the people there, and the article sent to them by itself.</p>
        </div>
      </div>
      <div className="gtlx-switch" role="tablist" aria-label="Auto outreach">
        <button type="button" role="tab" aria-selected={t === 'hiring'} className={'gtlx-sw' + (t === 'hiring' ? ' gtlx-sw--on' : '')} style={{ paddingLeft: 14 }} onClick={() => navigate('/outreach')}>Hiring now<em>from the weekly check</em></button>
        <button type="button" role="tab" aria-selected={t === 'people'} className={'gtlx-sw' + (t === 'people' ? ' gtlx-sw--on' : '')} style={{ paddingLeft: 14 }} onClick={() => navigate('/outreach/people')}>People<em>who to send to</em></button>
        <button type="button" role="tab" aria-selected={t === 'sent'} className={'gtlx-sw' + (t === 'sent' ? ' gtlx-sw--on' : '')} style={{ paddingLeft: 14 }} onClick={() => navigate('/outreach/sent')}>Sent<em>emails that went</em></button>
        <button type="button" role="tab" aria-selected={t === 'not-sent'} className={'gtlx-sw' + (t === 'not-sent' ? ' gtlx-sw--on' : '')} style={{ paddingLeft: 14 }} onClick={() => navigate('/outreach/not-sent')}>Not sent<em>and why</em></button>
      </div>
      {t === 'people' ? <People authFetch={authFetch} navigate={navigate} /> : t === 'sent' ? <Sent authFetch={authFetch} navigate={navigate} /> : t === 'not-sent' ? <NotSent authFetch={authFetch} navigate={navigate} /> : <HiringNow authFetch={authFetch} navigate={navigate} />}
    </div>
  );
}
